// src/components/AdminNews.jsx
import React, { useEffect, useState } from "react";
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal
} from "react-bootstrap";

const emptyForm = {
  title: "",
  summary: "",
  content: "",
  imageUrl: "",
  pinned: false,
  published: true,
};

const AdminNews = () => {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [errorList, setErrorList] = useState("");

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [confirm, setConfirm] = useState({ show: false, id: null, title: "" });

  const fetchNews = async () => {
    setLoadingList(true);
    setErrorList("");
    try {
      const snap = await getDocs(collection(db, "news"));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Orden: pinned primero, luego fecha desc (publishedAt/createdAt)
      rows.sort((a, b) => {
        const ap = a?.pinned ? 1 : 0;
        const bp = b?.pinned ? 1 : 0;
        if (ap !== bp) return bp - ap;
        const ta = a?.publishedAt || a?.createdAt || null;
        const tb = b?.publishedAt || b?.createdAt || null;
        const da = ta?.toDate ? ta.toDate().getTime() : (ta ? new Date(ta).getTime() : 0);
        const dbb = tb?.toDate ? tb.toDate().getTime() : (tb ? new Date(tb).getTime() : 0);
        return dbb - da;
      });
      setItems(rows);
    } catch (e) {
      console.error(e);
      setErrorList("No se pudieron cargar las noticias.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onEdit = (news) => {
    setEditingId(news.id);
    setForm({
      title: news.title || "",
      summary: news.summary || "",
      content: news.content || "",
      imageUrl: news.imageUrl || "",
      pinned: !!news.pinned,
      published: news.published !== false,
    });
    window.scrollTo(0, 0);
  };

  const onDelete = (news) => {
    setConfirm({ show: true, id: news.id, title: news.title || "" });
  };

  const doDelete = async () => {
    const id = confirm.id;
    setConfirm({ show: false, id: null, title: "" });
    if (!id) return;
    try {
      await deleteDoc(doc(db, "news", id));
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar la noticia.");
    }
  };

  const onCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert("El título es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        // actualizar
        await updateDoc(doc(db, "news", editingId), {
          title: form.title.trim(),
          summary: form.summary.trim(),
          content: form.content.trim(),
          imageUrl: form.imageUrl.trim(),
          pinned: !!form.pinned,
          published: !!form.published,
          updatedAt: serverTimestamp(),
          // si se marca publicado y no tiene publishedAt, lo fijamos
          ...(form.published ? { publishedAt: serverTimestamp() } : {}),
        });
      } else {
        // crear
        await addDoc(collection(db, "news"), {
          title: form.title.trim(),
          summary: form.summary.trim(),
          content: form.content.trim(),
          imageUrl: form.imageUrl.trim(), // opcional
          pinned: !!form.pinned,
          published: !!form.published,
          createdAt: serverTimestamp(),
          ...(form.published ? { publishedAt: serverTimestamp() } : {}),
        });
      }
      onCancel();
      fetchNews();
    } catch (e) {
      console.error(e);
      alert("No se pudo guardar la noticia.");
    } finally {
      setSaving(false);
    }
  };

  const togglePinned = async (news) => {
    try {
      await updateDoc(doc(db, "news", news.id), {
        pinned: !news.pinned,
        updatedAt: serverTimestamp(),
      });
      setItems(prev => prev.map(i => i.id === news.id ? { ...i, pinned: !i.pinned } : i));
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar el destacado.");
    }
  };

  const togglePublished = async (news) => {
    const next = !(news.published !== false);
    try {
      await updateDoc(doc(db, "news", news.id), {
        published: next,
        updatedAt: serverTimestamp(),
        ...(next ? { publishedAt: serverTimestamp() } : {}),
      });
      setItems(prev => prev.map(i =>
        i.id === news.id ? { ...i, published: next, publishedAt: next ? new Date() : i.publishedAt } : i
      ));
    } catch (e) {
      console.error(e);
      alert("No se pudo cambiar el estado de publicación.");
    }
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Administrar Noticias</h1>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={onSubmit}>
            <Row className="g-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Título *</Form.Label>
                  <Form.Control
                    name="title"
                    value={form.title}
                    onChange={onChange}
                    placeholder="Título de la noticia"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end gap-3">
                <Form.Check
                  type="switch"
                  id="pinned"
                  name="pinned"
                  label="Destacada"
                  checked={form.pinned}
                  onChange={onChange}
                />
                <Form.Check
                  type="switch"
                  id="published"
                  name="published"
                  label="Publicada"
                  checked={form.published}
                  onChange={onChange}
                />
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Resumen</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="summary"
                    value={form.summary}
                    onChange={onChange}
                    placeholder="Resumen corto para portada"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Contenido</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="content"
                    value={form.content}
                    onChange={onChange}
                    placeholder="Contenido completo"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Imagen (URL pública)</Form.Label>
                  <Form.Control
                    name="imageUrl"
                    value={form.imageUrl}
                    onChange={onChange}
                    placeholder="https://..."
                  />
                  <Form.Text className="text-muted">
                    Si prefieres subir a Storage, podemos agregarlo luego; por ahora pega una URL pública.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="mt-3 d-flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Crear noticia"}
              </Button>
              {editingId && (
                <Button variant="outline-secondary" onClick={onCancel} disabled={saving}>
                  Cancelar edición
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      <h4 className="mb-3">Listado</h4>

      {errorList && (
        <Alert variant="warning" onClose={() => setErrorList("")} dismissible>
          {errorList}
        </Alert>
      )}

      {loadingList ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : items.length === 0 ? (
        <Card><Card.Body className="text-muted text-center">No hay noticias.</Card.Body></Card>
      ) : (
        <Row xs={1} md={2} className="g-3">
          {items.map((n) => (
            <Col key={n.id}>
              <Card className="h-100">
                {n.imageUrl ? (
                  <div style={{ height: 160, overflow: "hidden", background: "#f8f9fa" }}>
                    <img
                      src={n.imageUrl}
                      alt={n.title || "Noticia"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                ) : null}
                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start">
                    <Card.Title className="me-2">{n.title || "Sin título"}</Card.Title>
                    <div className="d-flex gap-2">
                      {n.pinned && <Badge bg="info">Destacada</Badge>}
                      {(n.published !== false) ? (
                        <Badge bg="success">Publicado</Badge>
                      ) : (
                        <Badge bg="secondary">Borrador</Badge>
                      )}
                    </div>
                  </div>
                  {n.summary && <Card.Text className="text-muted mt-2">{n.summary}</Card.Text>}
                  <div className="mt-auto d-flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-info" onClick={() => togglePinned(n)}>
                      {n.pinned ? "Quitar destacada" : "Destacar"}
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => togglePublished(n)}>
                      {(n.published !== false) ? "Pasar a borrador" : "Publicar"}
                    </Button>
                    <Button size="sm" onClick={() => onEdit(n)}>Editar</Button>
                    <Button size="sm" variant="danger" onClick={() => onDelete(n)}>Eliminar</Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={confirm.show} onHide={() => setConfirm({ show: false, id: null, title: "" })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar noticia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Seguro que deseas eliminar <strong>{confirm.title || "esta noticia"}</strong>? Esta acción no se
          puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirm({ show: false, id: null, title: "" })}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={doDelete}>Eliminar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminNews;
